import { isEmpty, size } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import Page from '@/components/Page'
import SuccessConfirmation from '@/components/SuccessConfirmation'
import { resetRequest } from '@/store/actions'
import { isRequestError, isRequestPending, isRequestSuccessful } from '@/store/selectors'

import {
  addCustomAppInstance,
  clearCurrentApp,
  deleteCustomAppInstance,
  fetchCustomAppInstance,
  setCurrentApp,
  setCurrentConnector,
  setCurrentMicroApps,
  updateCustomAppInstance
} from './actions'
import {
  ADD_CUSTOM_APP_INSTANCE,
  DELETE_CUSTOM_APP_INSTANCE,
  FETCH_CUSTOM_APP_INSTANCE,
  UPDATE_CUSTOM_APP_INSTANCE
} from './actionTypes'
import CustomAppEditView from './CustomAppEditView'
import messages from './messages'
import NewCustomConnectorView from './NewCustomConnectorView'
import { getClientCredential, getCustomApp, getCustomConnector, getCustomMicroapps } from './selectors'

import './index.scss'

import { usePrevious } from 'react-use'

import { getCurrentSpaceId } from '@/components/App/selectors'
import DeleteConfirmationWithInput from '@/components/DeleteConfirmationWithInput'

const errorsToHandle = [
  {
    type: FETCH_CUSTOM_APP_INSTANCE,
    data: { detailCode: 'APP_CUSTOM_INSTANCE' }
  },
  {
    type: DELETE_CUSTOM_APP_INSTANCE,
    data: { detailCode: 'DELETE_CUSTOM_INSTANCE' }
  },
  {
    type: UPDATE_CUSTOM_APP_INSTANCE,
    data: { detailCode: 'UPDATE_CUSTOM_INSTANCE' }
  },
  {
    type: ADD_CUSTOM_APP_INSTANCE,
    data: { detailCode: 'ADD_CUSTOM_INSTANCE' }
  }
]

function SpaceCustomAppEditPage(props: any) {
  const [state, _setState] = useState({
    isEditing: false,
    showDeleteModal: false
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  const location = useLocation()
  const history = useHistory()
  const params = useParams<{ appId: string }>()

  // componentDidMount
  useEffect(() => {
    props.actions.resetRequest([ADD_CUSTOM_APP_INSTANCE, UPDATE_CUSTOM_APP_INSTANCE, DELETE_CUSTOM_APP_INSTANCE])

    if (params.appId) {
      props.actions.fetchCustomAppInstance(params.appId)
      loadAppFromRoute()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate
  const prevProps = usePrevious(props)
  const prevParams = usePrevious(params)
  useEffect(() => {
    if (!prevProps || !prevParams) return

    const spaceChange = props.currentSpaceId !== prevProps.currentSpaceId
    const fetchFailed = prevProps.isLoading && !props.isLoading && isEmpty(props.currentApp)
    const appDeleted = props.isDeleteSuccessful

    if (spaceChange || fetchFailed) {
      goBackToInstalledApps()
    }

    if (
      params.appId &&
      ((prevProps.currentApp.id !== props.currentApp.id && !appDeleted) || prevParams.appId !== params.appId)
    ) {
      loadAppFromRoute()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, prevProps])

  // componentWillUnmount
  useEffect(() => {
    return () => {
      props.actions.clearCurrentApp()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadAppFromRoute = async () => {
    if (props.currentApp) {
      setState({
        isEditing: true
      })

      if (props.currentApp.connectorConfig && props.currentApp.connectorId) {
        props.actions.setCurrentConnector({
          ...props.currentApp.connectorConfig,
          id: props.currentApp.connectorId
        })
      }

      if (props.currentApp.microAppConfig && props.currentApp.microAppId) {
        props.actions.setCurrentMicroApps([
          {
            ...props.currentApp.microAppConfig,
            audience: props.currentApp.audience,
            id: props.currentApp.microAppId
          }
        ])
      }
    }
  }

  const getActionMessage = () => {
    if (props.isDeleting) {
      return <FormattedMessage {...messages.deleting} />
    } else {
      return <FormattedMessage {...messages.saving} />
    }
  }

  const generateView = () => {
    const { isEditing } = state
    const {
      currentApp,
      currentMicroapps,
      currentConnector,
      isDeleteSuccessful,
      isDeleting,
      isUpdateSuccessful,
      isUpdating,
      clientCredential,
      isInstalling,
      isInstallSuccessful
    } = props
    if ((isUpdateSuccessful || isInstallSuccessful) && size(currentApp.connectorConfig) !== 0 && clientCredential) {
      return (
        <NewCustomConnectorView
          connector={{ ...currentApp.connectorConfig, clientCredential: clientCredential }}
          okClick={onOk}
        />
      )
    } else if (isUpdateSuccessful || isInstallSuccessful || isDeleteSuccessful) {
      return (
        <SuccessConfirmation
          message={
            isDeleteSuccessful ? (
              <FormattedMessage {...messages.deleteSuccess} />
            ) : isUpdateSuccessful ? (
              <FormattedMessage {...messages.updateSuccess} />
            ) : (
              <FormattedMessage {...messages.installSuccess} />
            )
          }
          onBack={goBackToInstalledApps}
        />
      )
    } else {
      return (
        <CustomAppEditView
          currentApp={currentApp}
          currentMicroapp={currentMicroapps[0]}
          currentConnector={currentConnector}
          isActionInProgress={isInstalling || isDeleting || isUpdating}
          actionMessage={getActionMessage()}
          isEditing={isEditing}
          onDelete={onDeleteConfirm}
          onCancel={goBackToInstalledApps}
          submitApp={submitApp}
          onNavigateConnectorLogs={goToConnectorLogs}
        />
      )
    }
  }

  const onOk = () => {
    goBackToInstalledApps()
  }

  const goBackToInstalledApps = () => {
    const count = state.isEditing ? 3 : 1
    history.push(stripPath(location.pathname, count) + '/installed')
  }

  const goToConnectorLogs = () => {
    window.open(
      `${stripPath(window.location.href, 4)}/apps/${props.currentApp.id}/${props.currentApp.connectorId}/logs`
    )
  }

  function stripPath(path: any, count: any) {
    for (let i = 0; i < count; i++) {
      path = path.slice(0, Math.max(0, path.lastIndexOf('/')))
    }
    return path
  }

  const onDelete = async () => {
    props.actions.deleteCustomAppInstance(props.currentApp.id)
    setState({
      showDeleteModal: false
    })
  }

  const onDeleteCancel = () => {
    setState({
      showDeleteModal: false
    })
  }

  const onDeleteConfirm = () => {
    setState({
      showDeleteModal: true
    })
  }

  const submitApp = async (app: any) => {
    let appData: any = {
      name: app.currentApp.name,
      iconUrl: app.currentApp.iconUrl,
      id: app.currentApp.id,
      installConnector: false,
      installMicroApp: false
    }

    if (props.currentApp.connectorId) {
      appData = {
        ...appData,
        connectorId: props.currentApp.connectorId
      }
    }

    if (app.isSmartNotificationsExpanded) {
      appData = {
        ...appData,
        installConnector: true,
        connectorConfig: {
          iconUrl: app.currentConnector.iconUrl,
          name: app.currentConnector.name,
          webHookUrl: app.currentConnector.webHookUrl
        }
      }
    }

    if (props.currentApp.microAppId) {
      appData = {
        ...appData,
        microAppId: props.currentApp.microAppId
      }
    }

    if (app.isMicroUIExpanded) {
      appData = {
        ...appData,
        installMicroApp: true,
        audience: app.currentMicroapp.audience,
        microAppConfig: {
          configuration: app.currentMicroapp.configuration,
          iconUrl: app.currentApp.iconUrl,
          summary: app.currentMicroapp.summary,
          title: app.currentMicroapp.title,
          url: app.currentMicroapp.url
        }
      }

      if (!props.currentApp.microAppId) {
        // Keep the newly entered values for app ui form fields from clearing when submitting the form
        props.actions.setCurrentMicroApps([
          {
            ...appData.microAppConfig,
            audience: appData.audience
          }
        ])
      }
    }

    state.isEditing ? await onUpdate(appData) : await onInstall(appData)
  }

  const onInstall = async (appData: any) => {
    props.actions.addCustomAppInstance(appData)
  }

  const onUpdate = async (appData: any) => {
    props.actions.updateCustomAppInstance({
      ...appData
    })
  }

  const { showDeleteModal } = state
  const { currentApp } = props
  return (
    <Page
      className="space-custom-app-wrapper edit publisher-two-column-layout"
      isLoading={props.isLoading}
      errorsToHandle={errorsToHandle}
    >
      <Page.Header
        title={<FormattedMessage {...messages.title} />}
        goBack={goBackToInstalledApps}
        backNavTitle={<FormattedMessage {...messages.back} />}
      />
      {showDeleteModal && (
        <DeleteConfirmationWithInput
          onConfirm={onDelete}
          onCancel={onDeleteCancel}
          itemName={currentApp.name || ''}
          subMessage={<FormattedMessage {...messages.subMessage} />}
          warning={
            <FormattedMessage
              {...messages.warning}
              values={{ appName: <span className="confirm-app-name">{currentApp.name}</span> }}
            />
          }
          label={<FormattedMessage {...messages.confirmDelete} />}
          cancel={<FormattedMessage {...messages.cancel} />}
          deleteMessage={<FormattedMessage {...messages.delete} />}
          deleteButton={<FormattedMessage {...messages.deleteButton} />}
        />
      )}
      {generateView()}
    </Page>
  )
}

SpaceCustomAppEditPage.displayName = 'SpaceCustomAppEditPage'

SpaceCustomAppEditPage.propTypes = {
  actions: PropTypes.shape({
    addCustomAppInstance: PropTypes.func.isRequired,
    setCurrentApp: PropTypes.func.isRequired,
    fetchCustomAppInstance: PropTypes.func.isRequired,
    clearCurrentApp: PropTypes.func.isRequired,
    deleteCustomAppInstance: PropTypes.func.isRequired,
    updateCustomAppInstance: PropTypes.func.isRequired
  }).isRequired,
  errorMessage: PropTypes.object,
  currentApp: PropTypes.shape({
    iconUrl: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string
  }),
  currentMicroapps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      summary: PropTypes.string,
      url: PropTypes.string,
      iconUrl: PropTypes.string,
      audience: PropTypes.array,
      configuration: PropTypes.shape({
        allowDetailExpand: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        securityOptions: PropTypes.array,
        domainWhitelist: PropTypes.array,
        contact: PropTypes.shape({
          link: PropTypes.string
        })
      })
    })
  ),
  currentConnector: PropTypes.shape({
    clientCredential: PropTypes.shape({
      clientId: PropTypes.string,
      clientSecret: PropTypes.string
    }),
    iconUrl: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    webHookUrl: PropTypes.string,
    appId: PropTypes.string
  }),
  currentSpaceId: PropTypes.string.isRequired,
  hasError: PropTypes.bool.isRequired,
  isDeleteSuccessful: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isUpdateSuccessful: PropTypes.bool.isRequired,
  isUpdating: PropTypes.bool.isRequired
}

SpaceCustomAppEditPage.defaultProps = {
  errorMessage: {},
  currentApp: {},
  currentMicroapps: [],
  currentConnector: {}
}

export { /** @deprecated */ SpaceCustomAppEditPage }

function mapStateToProps(state: any) {
  return {
    currentApp: getCustomApp(state),
    currentSpaceId: getCurrentSpaceId(state),
    currentConnector: getCustomConnector(state),
    currentMicroapps: getCustomMicroapps(state),
    hasError: isRequestError(state, [
      FETCH_CUSTOM_APP_INSTANCE,
      DELETE_CUSTOM_APP_INSTANCE,
      ADD_CUSTOM_APP_INSTANCE,
      UPDATE_CUSTOM_APP_INSTANCE
    ]),
    isLoading: isRequestPending(state, FETCH_CUSTOM_APP_INSTANCE),
    isDeleteSuccessful: isRequestSuccessful(state, DELETE_CUSTOM_APP_INSTANCE),
    isDeleting: isRequestPending(state, DELETE_CUSTOM_APP_INSTANCE),
    isUpdateSuccessful: isRequestSuccessful(state, [UPDATE_CUSTOM_APP_INSTANCE]),
    isUpdating: isRequestPending(state, [UPDATE_CUSTOM_APP_INSTANCE]),
    isInstallSuccessful: isRequestSuccessful(state, [ADD_CUSTOM_APP_INSTANCE]),
    isInstalling: isRequestPending(state, [ADD_CUSTOM_APP_INSTANCE]),
    clientCredential: getClientCredential(state)
  }
}

function mapDispatchToProps(dispatch: any) {
  return {
    actions: bindActionCreators(
      {
        resetRequest,
        setCurrentApp,
        addCustomAppInstance,
        setCurrentConnector,
        setCurrentMicroApps,
        fetchCustomAppInstance,
        clearCurrentApp,
        deleteCustomAppInstance,
        updateCustomAppInstance
      },
      dispatch
    )
  }
}
export default connect(mapStateToProps, mapDispatchToProps)(SpaceCustomAppEditPage)
